import { ResidenceOwnership, TaskType } from '@servicetitan/financing-api-client';
import { ProviderFieldOverride } from './fieldRegistry';
import { FieldValueSet, FIRST_LOOK_RESIDENCE_OWNERSHIP } from './fieldValueSets';

// Configuration interface for financing providers
export interface ProviderConfig {
    // Human-readable provider name used for customer-facing copy (e.g. second-look UI).
    // Falls back to the raw provider name when not set.
    providerDisplayName?: string;
    // Determines if plan tags like "Lowest Payment" are displayed
    showPlanTags: boolean;
    // Custom legal disclosure text for deferred interest plans
    customLegalDisclosureTextForDeferredInterest?: string;
    // Controls whether to show the "How It Works" section
    enableHowItWorks?: boolean;
    // Category of legal disclosures to display for the provider
    legalDisclosureCategory?: string[] | string;
    // Sets initial workflow stage for application process
    defaultWorkflowStage: 'plan-selection' | 'external-application' | 'second-look-application';
    // Available tasks that can be performed with this provider
    availableTasks?: Partial<Record<TaskType, TaskConfig>>;
    // Used for homeowner external applications
    requiresSubmittingUser?: boolean;
    // Text for acknowledgement checkbox in application forms
    applicationAcknowledgementText?: string;
    // Text for the SMS-consent checkbox shown below the phone number (Synchrony)
    smsConsentText?: string;
    // Controls which application buttons appear in the UI
    applicationButtons?:
        | 'GetPrequalified'
        | 'ApplyNow'
        | 'GetPrequalifiedAndApplyNow'
        | 'None'
        | 'StartPrequal'
        | 'TextLink';
    // Whether to show the option to send application via email
    allowSendViaEmail?: boolean;
    // Whether to show the change order button on application details page
    allowChangeOrder?: boolean;
    // Whether to show credit score disclosure message
    showCreditScoreDisclosure?: boolean;
    // Whether to show address fields in the application form
    showAddressFields?: boolean;
    // Used for interpreting the term interval unit in the application 1 = month, 12 = year
    termMultiplier: 12 | 1;
    // Used for displaying provider program name instead of provider name
    providerProgramName?: string;
    // Used for customizing application heading
    applicationHeading?: string;
    // Used for customizing pre-qualification heading (when useFinancingSubmission is false)
    preQualificationHeading?: string;
    // Used for customizing application dialog heading
    applicationDialogHeading?: string;
    // Used for customizing application subheading
    applicationSubheadingAction?: string;
    // Overrides the full technician instruction text in the application dialog
    technicianInstructionText?: string;
    // Used for customizing credit score disclosure text
    creditScoreDisclosureText?: string;
    // Additional fields to include in the application form
    /** @deprecated Use personalInfoFieldOverrides and jobDetailsFieldOverrides instead */
    additionalApplicantFields?: string[];
    // Field override configuration for personal information fields
    personalInfoFieldOverrides?: ProviderFieldOverride;
    // Field override configuration for job details fields
    jobDetailsFieldOverrides?: ProviderFieldOverride;
    // Per-field allowed value sets for enumerated fields whose accepted domain is
    // provider-specific. Single source of truth for the form options, the validation schema,
    // and the outbound sanitization of these fields (see FieldValueSet). When a field is omitted
    // the first-look default applies.
    fieldValueSets?: {
        residenceType?: FieldValueSet<ResidenceOwnership>;
    };
    // Disclosure blocks to display for the provider
    disclosureBlocks?: ('turns' | 'serviceFinance' | 'greenSky')[];
    // Multiple disclosures to display for the provider
    mustAgreeToMultipleDisclosures?: boolean;
    // Controls whether plans can be edited during change orders
    canEditPlansOnChangeOrder?: boolean;
    // Controls whether to use the prequalification choice flow UI display
    usePrequalChoiceFlow?: boolean;
    // Controls whether to use the customer auth flow UI display
    useCustomerAuthFlow?: boolean;
    // Indicates if this is a first-look or second-look lender
    providerCategory: 'first-look' | 'second-look';
    // Instructions text to display above the plans list
    planSelectionInstructions?: string;
    // Alert text to display at the top of prequalification plan selection screen
    prequalAlertText?: string;
    // Indicate to hide deferred interest Max Apr (mostly for synchrony for now)
    hideDeferredInterestMaxApr?: boolean;
    // Indicate to hide max approved amount on application details page
    hideMaxApprovedAmount?: boolean;
    // Enable payment frequency tabs feature
    enablePaymentFrequencyTabs?: boolean;
    // Enables technician MID tabs in plan selection for multi-account providers (Synchrony/Wells Fargo)
    supportsMultipleRateSheets?: boolean;
    // Show amortization information in plan cards
    showAmortization?: boolean;
    // Show Tasks tab in post-application interface (defaults to true)
    showTasksTab?: boolean;
    // Show the Tasks section on the App-Management application details screen (defaults to true).
    // Independent of showTasksTab (which controls the technician post-application tab): providers
    // with no tasks workflow at all (e.g. Affirm) set this to false to hide the empty section,
    // while providers like FinanceIt keep the section to render their "View Checklist" UI.
    showAppMgmtTasksTab?: boolean;
    // Show the History section on the App-Management application details screen (defaults to true).
    // Providers with no meaningful history to display (e.g. Affirm, BuyFin) set this to false so the
    // section is hidden entirely rather than rendering an empty state.
    showAppMgmtHistory?: boolean;
    // Enable external prequalification flow instead of in-app plan selection
    enableExternalPrequalification?: boolean;
    // When true, the plan-selection "Select Plan" button becomes a direct "See If You Pre-qualify"
    // action: it submits an external prequalification and opens the returned provider-hosted URL,
    // fully bypassing the in-app application form (used by Affirm).
    directPrequalification?: boolean;
    // Skip plan selection and show external application modal immediately
    skipPlanSelection?: boolean;
    // Custom text for the send via email button
    sendViaEmailButtonText?: string;
    // Use annual income instead of monthly income for this provider
    useAnnualIncome?: boolean;
    // Optional: whether the application flow includes a review step
    hasReview?: boolean;
    // Indicates if this is a first-look or second-look lender
    statesWithSpouseInformation?: string[];
    // Legal disclosure category for phone info button
    phoneDisclosureCategory?: string;
    // Legal disclosure category for email information
    emailDisclosureCategory?: string;
    // Custom title for change order button
    changeOrderTitle?: string;
    // Whether to show the authorize payment button on application details page
    allowPaymentAuthorization?: boolean;
    // Reviewing submit configuration
    reviewingNow?: string;
    reviewingNowDescription?: string;
    reviewingNowComplete?: string;
    reviewingDeclined?: string;
    submittingLabel?: string;
    submittedLabel?: string;
    hasAsyncDecision?: boolean;
    // Message to display when application is approved
    approvedMessage?: string;
    declinedHeadline?: string;
    declinedMessage?: string;
    declinedAccessLinkLabel?: string;
    underReviewHeadline?: string;
    underReviewMessage?: string;
    // Whether to hide the pending decision screen while reviewing status
    hidePendingDecisionScreen?: boolean;
    // Whether the provider has dynamic disclosures
    hasDynamicDisclosures?: boolean;
    // Whether the provider has ID fields
    hasIdFields?: boolean;
    // Whether the provider's workflow renders a dedicated Mailing Address step
    // (vs. the compact flow with Job Details inline).
    hasMailingAddressStep?: boolean;
    // Whether the Job Details page offers a checkbox to enter a mailing/home address
    // distinct from the job address, so the residence isn't defaulted to the job site.
    allowMailingAddressOverride?: boolean;
    // Custom loan amount validation for providers with specific requirements
    loanAmountValidation?: {
        minAmount?: number;
        maxAmount?: number;
    };
    // Custom address validation for providers with specific requirements
    addressValidation?: {
        streetMaxLength?: number;
    };
    // Whether to use financing submission (false means use prequal submission)
    useFinancingSubmission?: boolean;
    /** Soft prequal via Financing POST /v1/loans/prequal (Service Finance CP2). */
    useFinancingPrequalSubmission?: boolean;
    // Controls whether to transition to hard app (plan-selection) after soft app approval
    continueToHardAppAfterSoft?: boolean;
    // Label for the soft-app approval button that transitions to the hard application
    continueToHardAppButtonText?: string;
    // Whether the provider has detailed marriage fields when workflow uses mailing address and is married - job details always shows these fields
    hasDetailedMarriageFields?: boolean;
    softAppApprovedHeadline?: string;
    softAppApprovedMessage?: string;
    // Controls whether to show soft app message even when prequalificationId exists
    showSoftAppMessageForPrequalification?: boolean;
    // Controls whether to show banner for existing approved loans in technician app
    showTechnicianLoanBanner?: boolean;
    // Controls whether to hide the dialog close button
    hideDialogCloseButton?: boolean;
    // Controls whether customer can accept offer on approval screen
    customerCanAcceptOffer?: boolean;
    // Turns co-application disclosure text if this is a Turns co-application.
    turnsCoapplicationDisclosureText?: string;
    // URL to the second-look provider's public terms and conditions PDF.
    // Displayed as a "View terms" link in co-application disclosures.
    coApplicationTermsUrl?: string;
    // Controls whether to use decision workflow (esign + signature steps) after approval
    useDecisionWorkflow?: boolean;
    // Controls whether the co-applicant step appears in the application form (defaults to true)
    showCoApplicant?: boolean;
    // When true, disclosures render inline in the Job Details step instead of as a separate step
    hasInlineDisclosures?: boolean;
    // Optional override for the submit button label on the last form step
    submitButtonLabel?: string;
    // Acknowledgement text shown when user is applying for prequalification (soft pull).
    // When set and isPrequalification is true, ExternalApplicationFields uses this instead of
    // applicationAcknowledgementText. Also used by WellsFargoDisclosure for inline disclosures.
    prequalAcknowledgementText?: string;
}

export interface TaskConfig {
    // Display title for the task
    title: string;
    // Description text explaining what the task does
    description: string;
    // Text to show in the task's button. Will use the title if this is null.
    buttonText?: string;
    // Whether this task has an associated URL to navigate to
    hasUrl?: boolean;
    // If true this task can be taken even if the previous one has not yet been completed.
    hasArbitraryOrder?: boolean;
    // This task is just showing as a status
    isStatus?: boolean;
    // An incomplete task with this flag gates every task after it: nothing downstream becomes
    // actionable until it completes. Distinct from isStatus, which only marks display-only tasks.
    blocksDownstreamTasks?: boolean;
    // When true the task is still shown but its action button is suppressed and the task is not
    // clickable. Used by role-specific configs (e.g. the Finturf technician variant) where the
    // office, not the technician, performs the action.
    hideActionButton?: boolean;
}

const turnsDeferredInterestDisclosure =
    '<strong>Deferred Interest</strong>: Interest will be charged to your account ' +
    'if the qualifying purchase balance is not paid in full within the promotional ' +
    'period or if you make a late payment. You must pay more than your required ' +
    'minimum payment to avoid interest. See Credit Terms for details.';

export const turns: ProviderConfig = {
    providerDisplayName: 'Turns',
    coApplicationTermsUrl:
        'https://turns.s3.us-east-1.amazonaws.com/Turns+Public+Terms+and+Conditions+082124.pdf',
    showPlanTags: true,
    defaultWorkflowStage: 'second-look-application',
    applicationButtons: 'ApplyNow',
    showCreditScoreDisclosure: true,
    termMultiplier: 1,
    // jobDetails is for section-level visibility, not field-level
    additionalApplicantFields: ['jobDetails'],
    personalInfoFieldOverrides: {
        additions: ['requestedLoanAmount', 'incomeType'],
    },
    jobDetailsFieldOverrides: {
        additions: ['industryType'],
    },
    disclosureBlocks: ['turns'],
    providerCategory: 'second-look',
    customLegalDisclosureTextForDeferredInterest: turnsDeferredInterestDisclosure,
    // Shown in the dialog header once the applicant selects a Turns offer after being declined
    // by a first-look provider (see useSecondLookPreQualificationHeading).
    preQualificationHeading: 'Turns Pre-Qualification Request',
};

/**
 * Finturf Second-Look Configuration
 *
 * Finturf is a second-look provider selectable via tenant settings
 * (`allowedOnlineEstimatesSecondLookProviders`). The shared workflow/decision code path resolves
 * the second-look provider dynamically, so switching a tenant from Turns to Finturf is a
 * settings-only change. This config supplies the Finturf-specific customer-facing copy.
 */
export const finturf: ProviderConfig = {
    providerDisplayName: 'Finturf',
    showPlanTags: true,
    defaultWorkflowStage: 'second-look-application',
    applicationButtons: 'ApplyNow',
    showCreditScoreDisclosure: true,
    termMultiplier: 1,
    additionalApplicantFields: ['jobDetails'],
    personalInfoFieldOverrides: {
        additions: ['requestedLoanAmount', 'incomeType'],
    },
    jobDetailsFieldOverrides: {
        additions: ['industryType'],
    },
    fieldValueSets: {
        residenceType: {
            allowed: [
                ...FIRST_LOOK_RESIDENCE_OWNERSHIP.allowed,
                ResidenceOwnership.LiveWithParents,
                ResidenceOwnership.Other,
            ],
            fallback: ResidenceOwnership.NotSet,
        },
    },
    providerCategory: 'second-look',
    // Shown in the dialog header once the applicant selects a Finturf offer after being declined
    // by a first-look provider (see useSecondLookPreQualificationHeading).
    preQualificationHeading: 'Finturf Pre-Qualification Request',
    // Select an Offer and NTP are status-only indicators; Upload Conditions and Request Funds link
    // out to the Finturf-hosted page. Select an Offer can appear pre-decision (e.g. Prequalified).
    availableTasks: {
        [TaskType.AcceptOffer]: {
            title: 'Select an Offer',
            description: 'The applicant must select an offer to move forward with the loan.',
            isStatus: true,
        },
        // Title intentionally differs from the `ActionRequired` key: that is the backend enum, but the
        // customer-facing copy for it is "Upload Conditions".
        [TaskType.ActionRequired]: {
            title: 'Upload Conditions',
            description: 'Submit documentation to resolve open conditions and stipulations.',
            hasUrl: true,
        },
        [TaskType.NtpCompleted]: {
            title: 'Notice to Proceed (NTP)',
            description:
                'NTP is automatically granted once all required outstanding tasks are completed.',
            isStatus: true,
            blocksDownstreamTasks: true,
        },
        [TaskType.FundRequest]: {
            title: 'Request Funds',
            description: 'When the job is complete, request funds to initiate the loan payout.',
            hasUrl: true,
        },
    },
};

// Suppress the action button on the listed tasks while leaving the rest (and any new tasks)
// untouched, so the technician variant stays in sync as Finturf's task set evolves.
const hideActionButtons = (
    tasks: NonNullable<ProviderConfig['availableTasks']>,
    hiddenTypes: TaskType[],
): NonNullable<ProviderConfig['availableTasks']> =>
    Object.fromEntries(
        Object.entries(tasks).map(([type, task]) => [
            type,
            hiddenTypes.includes(type as TaskType) ? { ...task!, hideActionButton: true } : task,
        ]),
    );

/**
 * Finturf Technician Variant
 *
 * Technicians can submit Upload Conditions but not Request Funds — the office handles funding — so
 * only the Request Funds action button is hidden in the technician app. Select an Offer and NTP stay
 * status-only.
 */
export const finturfTechnician: ProviderConfig = {
    ...finturf,
    availableTasks: hideActionButtons(finturf.availableTasks!, [TaskType.FundRequest]),
};

/**
 * ServiceFinance Embedded Flow Configuration
 *
 * ServiceFinance always uses the embedded flow with enhanced UX features.
 *
 * TURNS-specific features (controlled dynamically via turnsIntegration.isEnabled):
 * - Income type field: Added dynamically via usePersonalInfoFields
 * - TURNS disclosure: Shown dynamically when TURNS is fully enabled
 */
const serviceFinanceBase: ProviderConfig = {
    showPlanTags: true,
    defaultWorkflowStage: 'plan-selection',
    applicationButtons: 'None',
    showCreditScoreDisclosure: false,
    termMultiplier: 1,
    additionalApplicantFields: ['identification'],
    personalInfoFieldOverrides: {
        insertions: [
            { after: 'firstName', fields: ['middleInitial'] },
            { after: 'grossMonthlyIncome', fields: ['employerName'] },
        ],
        additions: ['isUSCitizenField'],
    },
    jobDetailsFieldOverrides: {
        additions: ['improvementType', 'isManufacturedHome'],
        removals: ['industryType'],
    },
    disclosureBlocks: ['serviceFinance'],
    providerCategory: 'first-look',
    availableTasks: {
        [TaskType.AddProjectInfo]: {
            title: 'Add Project Info',
            description: 'Add details about the work and equipment for this project.',
        },
        [TaskType.UploadConditions]: {
            title: 'Upload Conditions',
            description: 'Submit documentation to resolve open conditions and stipulations.',
            hasArbitraryOrder: true,
        },
        [TaskType.UploadHic]: {
            title: 'Upload HIC',
            description: "Upload the customer's signed home improvement contract.",
        },
        [TaskType.CustomerSignature]: {
            title: 'Customer Signatures',
            description: 'Review and sign the loan agreement.',
            buttonText: 'Sign',
            hasArbitraryOrder: true,
        },
        [TaskType.AuthorizeLoan]: {
            title: 'Dealer Authorization',
            description: 'View and sign the dealer authorization form.',
            buttonText: 'Authorize',
            hasArbitraryOrder: true,
        },
    },
    hideMaxApprovedAmount: true,
    allowChangeOrder: false,
    approvedMessage: 'Review your loan details and continue to sign and accept the offer.',
    declinedHeadline: 'Service Finance was not able to approve your application',
    declinedMessage:
        'Contact Service Finance at info@svcfin.com or (866) 254-0497 for more information.',
    underReviewHeadline: 'Your application is under review',
    underReviewMessage:
        'Contact Service Finance at info@svcfin.com or (866) 254-0497 for more information.',
    reviewingNowDescription: 'Checking if you qualify for Service Finance financing offers',
    reviewingNowComplete: 'Service Finance review complete',
    reviewingDeclined: 'Service Finance was not able to approve your application',
    hasIdFields: true,
    hasMailingAddressStep: false,
    allowMailingAddressOverride: true,
    statesWithSpouseInformation: ['WI', 'MO'],
    useDecisionWorkflow: true,
    turnsCoapplicationDisclosureText:
        'By checking this box and clicking "Submit Application", you are acknowledging that ' +
        'your demographic information will be forwarded to Turns Financing. Turns Financing ' +
        'will use the demographic information for purposes of completing a credit application. ',
};

/**
 * Service Finance soft prequal phase — slim form + POST /v1/loans/prequal.
 */
export const serviceFinancePrequal: ProviderConfig = {
    ...serviceFinanceBase,
    defaultWorkflowStage: 'plan-selection',
    applicationButtons: 'None',
    useFinancingSubmission: false,
    useFinancingPrequalSubmission: true,
    continueToHardAppAfterSoft: true,
    continueToHardAppButtonText: 'Complete Application',
    showCoApplicant: false,
    useDecisionWorkflow: false,
    hasAsyncDecision: true,
    additionalApplicantFields: [],
    personalInfoFieldOverrides: {
        insertions: [{ after: 'firstName', fields: ['middleInitial'] }],
    },
    preQualificationHeading: 'Service Finance Pre-Qualification Request',
    softAppApprovedHeadline: 'Congratulations! Your pre-qualification request was approved.',
    softAppApprovedMessage: 'Review your loan details and complete your credit application.',
    reviewingNowDescription: 'Checking if you qualify for Service Finance financing offers',
    reviewingNowComplete: 'Service Finance review complete',
    reviewingDeclined: 'Service Finance was not able to prequalify your application',
    declinedHeadline: 'Service Finance was not able to prequalify your application',
    declinedMessage:
        'Contact Service Finance at info@svcfin.com or (866) 254-0497 for more information.',
};

export const serviceFinance: ProviderConfig = {
    ...serviceFinanceBase,
};

const goodLeapBase: ProviderConfig = {
    showPlanTags: true,
    enableHowItWorks: true,
    defaultWorkflowStage: 'plan-selection',
    prequalAlertText:
        'Get a decision without impacting your credit score! You can change the amount and plan later.',
    requiresSubmittingUser: true,
    personalInfoFieldOverrides: {
        insertions: [
            { after: 'primaryPhone', fields: ['secondaryPhone'] },
            { after: 'pretaxIncome', fields: ['incomeType'] },
        ],
        additions: [
            'pretaxIncome',
            'typeOfHome',
            'spokenLanguage',
            'employmentStatus',
            'citizenshipStatus',
        ],
        removals: ['annualIncome'],
    },
    jobDetailsFieldOverrides: {
        additions: ['isManufacturedHome', 'mortgageMonthlyPayment', 'mortgageEstimatedBalance'],
    },
    applicationButtons: 'ApplyNow',
    emailDisclosureCategory: 'none',
    phoneDisclosureCategory: 'none',
    providerCategory: 'first-look',
    canEditPlansOnChangeOrder: true,
    useCustomerAuthFlow: false,
    hasDynamicDisclosures: true,
    useAnnualIncome: true,
    termMultiplier: 12,
    hasReview: true,
    useFinancingSubmission: true,
};

/**
 * GoodLeap configuration (embedded flow).
 *
 * GoodLeap always uses the embedded in-app flow. The Turns waterfall (submit to Turns after a
 * GoodLeap decline) only happens when Turns is fully enabled (requires BU + tenant settings).
 */
export const goodLeap: ProviderConfig = {
    ...goodLeapBase,
    availableTasks: {
        [TaskType.AddProjectInfo]: {
            title: 'Add Project Info',
            description: 'Add details about the work and equipment for this project.',
            hasArbitraryOrder: true,
        },
        [TaskType.UploadConditions]: {
            title: 'Upload Conditions',
            description: 'Submit documentation to resolve open conditions and stipulations.',
            hasUrl: true,
        },
        [TaskType.UploadHic]: {
            title: 'Upload HIC',
            description: "Upload the customer's signed home improvement contract.",
        },
        [TaskType.FinalizeLoanDetails]: {
            title: 'Finalize Loan Details',
            description: 'Review and submit finalized loan details to GoodLeap.',
        },
        [TaskType.NtpCompleted]: {
            title: 'Notice to Proceed (NTP)',
            description:
                'NTP is automatically granted once all required outstanding tasks are completed.',
            isStatus: true,
        },
    },
    // Decision copy (used during async review states in shared workflow UI)
    reviewingNow: 'GoodLeap is Reviewing Now',
    reviewingNowDescription: 'Checking if you qualify for GoodLeap financing offers.',
    reviewingNowComplete: 'GoodLeap Review Complete',
    reviewingDeclined: 'Unable to prequalify you.',
    declinedHeadline: 'GoodLeap was unable to prequalify your loan.',
    showTechnicianLoanBanner: true,
    hideDialogCloseButton: true,
    softAppApprovedHeadline: 'Congratulations! Your loan application was approved.',
    softAppApprovedMessage: 'You will receive an email to sign your loan documents.',
    showSoftAppMessageForPrequalification: true,
    turnsCoapplicationDisclosureText:
        'I also understand that if Goodleap has no available offers, the above information ' +
        'will be sent to Turns Financing, and Turns Financing may conduct a soft pull of my ' +
        'credit that will not affect my credit score.',
};

const greenSkyBase: Omit<
    ProviderConfig,
    'legalDisclosureCategory' | 'personalInfoFieldOverrides' | 'defaultWorkflowStage'
> = {
    showPlanTags: true,
    applicationButtons: 'StartPrequal',
    termMultiplier: 1,
    providerProgramName: 'the GreenSky® Program',
    applicationDialogHeading: 'GreenSky® Program Prequalification Request',
    applicationSubheadingAction: 'start the request',
    approvedMessage:
        'In order to activate your account and make a purchase, you must click "Accept Offer".',
    creditScoreDisclosureText: 'a prequalification request',
    disclosureBlocks: ['greenSky'],
    providerCategory: 'first-look',
    canEditPlansOnChangeOrder: true,
    customerCanAcceptOffer: true,
    useCustomerAuthFlow: true,
    mustAgreeToMultipleDisclosures: true,
    useAnnualIncome: true,
    allowSendViaEmail: false,
    // Minimum lowered to $100 so sub-$1,000 estimates still reach the calculation endpoint,
    // letting second-look providers (e.g. Affirm) be surfaced. Max stays at the GreenSky prequal
    // cap of $200,000.
    loanAmountValidation: {
        minAmount: 100,
        maxAmount: 200000,
    },
};

/**
 * GreenSky Embedded Flow Configuration
 *
 * This config is used for GreenSky's embedded application flow (with PIN challenge).
 * The embedded flow is independent of whether Turns is actually enabled
 * (which requires additional BU/tenant settings).
 *
 * - Flow: Embedded in-app with PIN challenge
 * - Turns: Optional fallback on decline (if fully enabled)
 */
export const greenSkyEmbedded: ProviderConfig = {
    ...greenSkyBase,
    defaultWorkflowStage: 'plan-selection',
    legalDisclosureCategory: ['footerImage', 'footer'],
    personalInfoFieldOverrides: {
        additions: ['spokenLanguage'],
    },
    statesWithSpouseInformation: ['WI'],
    phoneDisclosureCategory: 'phoneInfoButton',
    emailDisclosureCategory: 'emailInformation',
    reviewingNow: 'GreenSky® is Reviewing Now',
    reviewingNowDescription: 'Checking if you qualify for GreenSky® financing offers.',
    reviewingNowComplete: 'GreenSky® Review Complete',
    reviewingDeclined: 'Unable to prequalify you.',
    declinedHeadline: 'GreenSky® was unable to prequalify your loan.',
    declinedMessage:
        'You will receive a letter within 30 days with the results of the credit review.',
    hasAsyncDecision: true,
    underReviewHeadline: 'Your application is pending',
    hidePendingDecisionScreen: true,
    hasMailingAddressStep: false,
    useFinancingSubmission: false,
    useFinancingPrequalSubmission: true,
    preQualificationHeading: 'GreenSky® Program Pre-Qualification Request',
    continueToHardAppAfterSoft: true,
    continueToHardAppButtonText: 'Continue to Application',
    softAppApprovedHeadline:
        'Congratulations! Your pre-qualification request was approved by GreenSky® Program.',
    softAppApprovedMessage: 'Review your loan details and complete your credit application.',
    customLegalDisclosureTextForDeferredInterest: turnsDeferredInterestDisclosure,
    // Minimum lowered to $100 so sub-$1,000 estimates still reach the calculation endpoint,
    // letting second-look providers (e.g. Affirm) be surfaced. Max stays at the GreenSky prequal
    // cap of $200,000.
    loanAmountValidation: {
        minAmount: 100,
        maxAmount: 200000,
    },
    hasDetailedMarriageFields: true,
    turnsCoapplicationDisclosureText:
        'I also understand that if GreenSky has no available offers, the above information ' +
        'will be sent to Turns Financing, and Turns Financing may conduct a soft pull of my ' +
        'credit that will not affect my credit score.',
    availableTasks: {
        [TaskType.AcceptOffer]: {
            title: 'Accept Offer',
            description:
                'The customer must click the Accept Offer button to move forward with activating the loan.',
            buttonText: 'Accept Offer',
        },
        [TaskType.SendAccountActivation]: {
            title: 'Account Activation Email',
            isStatus: true,
            description:
                'An activation email is automatically sent to the customer with details on how to activate their GreenSky account.',
        },
        [TaskType.CompleteAccountActivation]: {
            title: 'Account Activated',
            isStatus: true,
            description:
                'The customer must follow the steps outlined in the email to activate their GreenSky account.',
        },
    },
};

/**
 * GreenSky Embedded Flow Configuration (Technician Variant)
 *
 * See greenSkyEmbedded comment - this is the technician-specific version of the embedded flow.
 */
export const greenSkyEmbeddedTechnician: ProviderConfig = {
    ...greenSkyEmbedded,
    useCustomerAuthFlow: false,
    useFinancingSubmission: false,
    preQualificationHeading: 'GreenSky® Program Pre-Qualification Request',
};

export const greenskyPrequalification: ProviderConfig = {
    ...greenSkyEmbedded,
    hasMailingAddressStep: true,
    personalInfoFieldOverrides: {
        additions: ['spokenLanguage'],
    },
    hasDetailedMarriageFields: true,
};

export const synchrony: ProviderConfig = {
    showPlanTags: true,
    planSelectionInstructions: 'Estimated payments shown.',
    defaultWorkflowStage: 'external-application',
    applicationButtons: 'StartPrequal',
    termMultiplier: 1,
    applicationHeading: 'Review Synchrony Financing Options',
    personalInfoFieldOverrides: {
        insertions: [{ after: 'firstName', fields: ['middleInitial'] }],
    },
    providerCategory: 'first-look',
    canEditPlansOnChangeOrder: true,
    availableTasks: {
        [TaskType.Authorize]: {
            title: 'Authorize Payment',
            description: 'Provide job information to securely authorize a payment.',
        },
        [TaskType.SendSalesSlip]: {
            title: 'Send Sales Slip',
            description: 'Email the customer the Sales Slip to review and sign.',
        },
        [TaskType.CustomerSignature]: {
            title: 'Customer Signature',
            description: 'The customer must e-sign the sales slip sent to their email.',
            isStatus: true,
        },
    },
    hideDeferredInterestMaxApr: true,
    allowChangeOrder: false,
    supportsMultipleRateSheets: true,
    smsConsentText:
        'By selecting this checkbox, you agree to the following: By providing your mobile ' +
        'phone number, you are consenting to receive a text containing application information ' +
        'at this number. You understand and agree that the text may be made by an automatic ' +
        'telephone dialing system from Synchrony, its affiliates, or agents. Message and data ' +
        'rates may apply. You can visit synchrony.com/legal/terms-of-use and choose "Synchrony ' +
        'Bank SMS Terms and conditions" to see terms and conditions or ' +
        'synchrony.com/legal/privacy-policy for privacy information.',
};

export const synchronyPrequalification: ProviderConfig = {
    ...synchrony,
    applicationDialogHeading: 'See if you Pre-qualify for Synchrony® Financing Options',
};

export const synchronyTechnician: ProviderConfig = {
    ...synchrony,
    applicationButtons: 'TextLink',
    allowSendViaEmail: true,
    sendViaEmailButtonText: 'Email Link',
};

export const wellsFargo: ProviderConfig = {
    showPlanTags: true,
    defaultWorkflowStage: 'external-application',
    applicationAcknowledgementText: `I, the applicant, acknowledge that by clicking the
    buttons below, my demographic information will be forwarded to Wells Fargo Bank, N.A.
    ("Wells Fargo"). Wells Fargo will use my demographic information for purposes of
    completing a credit application.`,
    allowSendViaEmail: false,
    showCreditScoreDisclosure: false,
    showAddressFields: true,
    termMultiplier: 1,
    personalInfoFieldOverrides: {
        insertions: [{ after: 'firstName', fields: ['middleInitial'] }],
    },
    canEditPlansOnChangeOrder: true,
    usePrequalChoiceFlow: true,
    providerCategory: 'first-look',
    availableTasks: {
        [TaskType.AuthorizeLoan]: {
            title: 'Dealer Authorization',
            description: 'View and sign the dealer authorization form.',
            buttonText: 'Authorize',
            hasArbitraryOrder: true,
        },
    },
    allowChangeOrder: false,
    allowPaymentAuthorization: true,
    supportsMultipleRateSheets: true,
    addressValidation: {
        streetMaxLength: 26,
    },
    prequalAcknowledgementText:
        'By checking this box, I understand this is not a credit card application and ' +
        'is only being used to see if any pre-qualified offers are available. I also ' +
        'understand Wells Fargo Bank, N. A. will conduct a soft pull of my credit and ' +
        'this will not affect my credit score.',
};

export const wellsFargoPrequalWithTurns: ProviderConfig = {
    ...wellsFargo,
    personalInfoFieldOverrides: {
        additions: ['incomeType'],
    },
    hasMailingAddressStep: false,
    showCoApplicant: false,
    hasInlineDisclosures: true,
    submitButtonLabel: 'See If You Pre-qualify',
    customLegalDisclosureTextForDeferredInterest: turnsDeferredInterestDisclosure,
    turnsCoapplicationDisclosureText:
        'I also understand that if Wells Fargo Bank, N. A. has no available offers, ' +
        'ServiceTitan will send the above information to secondary lenders. Ownership ' +
        'Type, Income Type, and Improvement Type will not be shared with Wells Fargo.',
    submittingLabel: 'Submitting Pre-qualification Request',
    submittedLabel: 'Pre-qualification Request Submitted',
    reviewingNow: 'Wells Fargo is Reviewing Now',
    reviewingNowDescription: 'Checking if you qualify for Wells Fargo pre-qualified offers',
    reviewingNowComplete: 'Wells Fargo Review Complete',
    reviewingDeclined: 'Unable to pre-qualify you.',
    declinedHeadline: 'Wells Fargo was not able to pre-qualify you.',
    declinedMessage:
        'We are sorry but we are not able to provide you with a pre-qualified offer at this time.',
    declinedAccessLinkLabel: 'Wells Fargo Application',
};

export const financeit: ProviderConfig = {
    termMultiplier: 1,
    showPlanTags: true,
    defaultWorkflowStage: 'external-application',
    providerCategory: 'first-look',
    enablePaymentFrequencyTabs: true,
    showAmortization: true,
    applicationButtons: 'StartPrequal',
    requiresSubmittingUser: false,
    enableExternalPrequalification: true,
    planSelectionInstructions:
        'Estimated payments shown. Final terms will be provided in your loan agreement.',
    showTasksTab: false, // FinanceIt also uses this to control the flow in post-app for tech where we dont want to show the tasks tab
    changeOrderTitle: 'View Checklist',
};

export const buyFin: ProviderConfig = {
    termMultiplier: 1,
    showPlanTags: true,
    defaultWorkflowStage: 'external-application',
    providerCategory: 'first-look',
    providerProgramName: 'BuyFin',
    applicationButtons: 'ApplyNow',
    requiresSubmittingUser: true,
    skipPlanSelection: true,
    planSelectionInstructions:
        'Estimated payments shown. Final terms will be provided in your loan agreement.',
    showTasksTab: true,
    showAppMgmtHistory: false, // BuyFin has no meaningful history to display — hide the section
};

export const buyFinTechnician: ProviderConfig = {
    ...buyFin,
    applicationButtons: 'TextLink',
    allowSendViaEmail: true,
    sendViaEmailButtonText: 'Email Link',
    technicianInstructionText:
        "Confirm the customer's information and send them a link to start the BuyFin application.",
};

export const affirm: ProviderConfig = {
    termMultiplier: 1,
    showPlanTags: true,
    defaultWorkflowStage: 'external-application',
    providerCategory: 'first-look',
    skipPlanSelection: true,
    directPrequalification: true, // Plan-selection button submits prequalification directly and opens the Affirm-hosted URL
    showTasksTab: false,
    showAppMgmtTasksTab: false, // Affirm has no tasks workflow — hide the App-Management Tasks section
    showAppMgmtHistory: false, // Affirm has no meaningful history to display — hide the section
    applicationButtons: 'StartPrequal',
    allowSendViaEmail: false,
    showCreditScoreDisclosure: true,
};
